import { Alert, Avatar, Button, Card, Divider, message, Spin, Tooltip } from "antd";
import Modal from "antd/lib/modal/Modal";
import { IRESTClient, RESTClientImpl } from "../services/RESTClient"
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { 
    setAssignedAgent,
    setLoginUrlExpire,
    setNumberLeaseExpire,
    setPhoneNumber,
    setRenewalAuthCode,
    setLoginUrl,
    getAssignedAgent,
    getPhoneNumber,
    getRenewalAuthCode,
    getLoginUrl } from "../../app/appSlice";
import { UserOutlined } from "@ant-design/icons";
import Cookies from "universal-cookie";

export interface CHCContentProps {
    client: IRESTClient
}

export const CHCContent = (props: CHCContentProps) => {
    const {client} = props;
    const dispatch = useDispatch();

    const [dataLoading, setDataLoading] = useState(false);
    const assignedAgent = useSelector(getAssignedAgent);
    const renewalAuthCode = useSelector(getRenewalAuthCode);
    const phoneNumber = useSelector(getPhoneNumber);
    const loginUrl = useSelector(getLoginUrl);
    var greeting = assignedAgent? `Welcome back ${assignedAgent}!` : "Request a temporary agent account for testing."
    const [alertMessage, _setAlertMessage] = useState(greeting);
    const cookies = useMemo(() => new Cookies(), []);

    const CompleteStateUpdate = useCallback((response: any) => {
        dispatch(setNumberLeaseExpire(response['numberLeaseExpiresGMT']));
        dispatch(setAssignedAgent(response['agentAssigned']));
        dispatch(setPhoneNumber(response['phoneNumber']));
        dispatch(setRenewalAuthCode(response['renewalAuthCode']));
        dispatch(setLoginUrl(response['loginUrl']));
        dispatch(setLoginUrlExpire(response['loginUrlExpiresGMT']));
    },[dispatch]);

    const OnGetNumber = async () => {
        setDataLoading(true);
        const idToken = RESTClientImpl.GetIdToken();
        if (idToken) {
            let response = await client.GetNumber(idToken);
            if(response['ErrorMessage'])
            {
                setDataLoading(false);
                _setAlertMessage(response['ErrorMessage']);
                return;
            }
            if(response['agentAssigned']) {
                CompleteStateUpdate(response);
                var leaseExpire = response['numberLeaseExpiresGMT'];
                setDataLoading(false);
                cookies.set('Lease', {'phoneNumber':response['phoneNumber'], 'renewalCode': response['renewalAuthCode']}, { path: '/', expires: new Date(leaseExpire)});
                _setAlertMessage(`Your lease is valid until ${leaseExpire}. Use the link below to open the Connect Console`);
            }
            return;
        }
    }

    const OnRenewNumber = useCallback( async () => {
        setDataLoading(true);
        const idToken = RESTClientImpl.GetIdToken();
        if (!idToken && !phoneNumber && !renewalAuthCode) {
            return;
        }

        let response = await client.RenewNumber(phoneNumber!, renewalAuthCode!, idToken!);
        if(response['ErrorMessage'])
        {
            setDataLoading(false);
            _setAlertMessage(response['ErrorMessage']);
            message.error('Unable to Renew Lease.');
            return;
        }

        if(response['agentAssigned']) {
            CompleteStateUpdate(response);
            var leaseExpire = response['numberLeaseExpiresGMT'];
            setDataLoading(false);
            cookies.set('Lease', {'phoneNumber':response['phoneNumber'], 'renewalCode': response['renewalAuthCode']}, { path: '/', expires: new Date(leaseExpire)});
            _setAlertMessage(`Lease has been renewed until ${leaseExpire}. Use the link below to open the Connect Console`);
            message.success('Lease Renewed.');
            return;
        }

        return;
    },[CompleteStateUpdate, client, phoneNumber, renewalAuthCode, cookies]);

    const OnCopyToClipboard = async () => {
        message.success('Copied to Clipboard!');
        navigator.clipboard.writeText(loginUrl!);
    }

    useEffect(() => {
        if(RESTClientImpl.GetIdToken() && !assignedAgent) {
            if(cookies.get('Lease')) {
                const leaseCookie = cookies.get('Lease');
                dispatch(setPhoneNumber(leaseCookie['phoneNumber']));
                dispatch(setRenewalAuthCode(leaseCookie['renewalCode']));
                OnRenewNumber();
            }
        }
    },[assignedAgent, cookies, dispatch, OnRenewNumber]);

    const roundButtons: React.CSSProperties = {
        borderRadius: '10px',
        margin:'2px'
    }

    const roundCards: React.CSSProperties = {
        borderRadius: '10px',
        margin:'2px',
        display:'flex',
        flexDirection:'column',
        justifyContent:'space-evenly',
        alignContent:'center',
        alignItems:'center',
        textAlign:'center'
    }

    return(
        <Modal 
            open={true}
            closable={false}
            footer={false}>
            <Spin tip="Updating..." spinning={dataLoading}>
                <Card
                    style={roundCards}
                    title="Anonymizer Utility">
                    <Alert style={{textAlign:'center'}} type='info' message={alertMessage}/>
                    <div style={{ 
                        fontSize:'22px',
                        margin: '2px',
                        fontWeight:'bold',
                        display:'flex',
                        flexDirection:'column',
                        justifyContent:'space-evenly',
                        }}>
                        <div style={roundCards}>
                            {assignedAgent && <Avatar size={128} icon={<UserOutlined/>}/>}
                            {assignedAgent && <div style={{marginTop:'10px'}} >{assignedAgent}</div>}
                            {phoneNumber && <div style={{marginTop:'10px'}}>{phoneNumber}</div>}
                            <Divider/>
                            <div style={{...roundCards, flexDirection:'column', marginTop:'10px'}}>
                                {loginUrl &&
                                    <Tooltip title='Click to copy the login url to your clipboard.'>
                                        <Button type='link' style={roundButtons} onClick={OnCopyToClipboard}>Copy Sign In Url to Clipboard</Button>
                                    </Tooltip>}
                                {loginUrl &&
                                    <Tooltip title='Click to open the connect console.'>
                                        <Button type='primary' style={roundButtons} onClick={() => window.open(loginUrl)}>Open Console</Button>
                                    </Tooltip>}
                                {!phoneNumber &&
                                    <Tooltip title='Request a temporary anonymous agent session.'>
                                        <Button type='primary' style={roundButtons} onClick={OnGetNumber}>Get Number Lease</Button>
                                    </Tooltip>}
                                {phoneNumber && renewalAuthCode &&
                                    <Tooltip title='Request a new lease for the current session.'>
                                        <Button danger type='ghost' style={roundButtons} onClick={OnRenewNumber}>Renew Lease</Button>
                                    </Tooltip>}    
                            </div>
                        </div>
                    </div>
                </Card>
            </Spin>
        </Modal>
    );
}